import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import {
    inColumns,
    readColumns,
    sumColumnsLen
} from '~/components/ListGrid3/Framework/columns';

import Memoized from '~/components/ListGrid3/Elements/Memoized';

import NoDataToShow from '~/containers/NoDataToShow/NoDataToShow';
import NoResult from '~/containers/NoResult/NoResult';

// prettier-ignore
const ListGrid3 = props => {
    const {
        'data-testid': testid,
        onSortChange,
        multiColumnSortOptions = {
            keepSameLen: true,
            algorithmId: 0
        },
        isDropDisabled,
        isDraggable,
        getDragging,
        filterString,
        handleNoItems,
        handleRenderer,
        itemsAreClickable,
        itemIsActiveCheck,
        itemRenderer,
        className,
        numCol,
        items,
        kind,
        gap
    } = props;

    const referred = useRef({}).current;
    const [state, setState] = useState(referred.state = {});
    const { assign } = window.Object;
    const updateState = update => {
        const prevState = referred.state;
        const newState = assign({}, prevState, update);
        // console.log({ prevState, newState });
        setState((referred.state = newState));
    };

    useEffect(() => {
        const chuncks = referred.state.chuncks;
        updateState({
            chuncks: inColumns(
                chuncks
                    ? inColumns(readColumns(chuncks))
                    : items,
                numCol
            )
        });
    }, [
        numCol,
        items,
        multiColumnSortOptions?.keepSameLen
    ]);

    const isMultiColumn = 1 < numCol;
    const count = sumColumnsLen(state.chuncks);
    // console.count('ListGrid');
    return (
        <div
            data-testid={testid || null}
            data-slots={count}
            className={
                `list-grid-3 ${
                    className || 'standard'
                } ${
                    isMultiColumn ? 'multi' : 'single'
                }-column ${
                    isDraggable ? 'is' : 'not'
                }-draggable items-${
                    itemsAreClickable ? 'are' : 'not'
                }-clickable ${
                    kind || 'generic'
                }-items`
            }
            style={{
                '--numCol': count ? numCol : 1,
                '--gap': gap || '.75rem'
            }}
        >
            {count ? (
                <DragDropContext
                    onBeforeDragStart={() => {
                        window.getSelection?.().removeAllRanges();
                    }}
                    onDragStart={event => {
                        getDragging?.(true, event);
                    }}
                    onDragEnd={event => {
                        if (event.destination) {
                            const {
                                index: sourceIndex,
                                droppableId: sourceId
                            } = event.source;
                            const {
                                index: destinationIndex,
                                droppableId: destinationId
                            } = event.destination;
                            if (
                                destinationId !== sourceId ||
                                destinationIndex !== sourceIndex
                            ) {
                                const cols = state.chuncks || [];
                                const colsLen = cols.length;
                                if (colsLen) {
                                    const toNum = str => parseInt(str.substring(6));
                                    const sourceColNum = toNum(sourceId);
                                    const fromColumn = cols[sourceColNum];
                                    const itemToMove = fromColumn[sourceIndex];
                                    if (itemToMove) {
                                        const destinationColNum = toNum(destinationId);
                                        const toColumn = cols[destinationColNum];
                                        if (toColumn) {
                                            const itemDestination = toColumn[destinationIndex];
                                            let newChuncks;
                                            let flatten;
                                            let moved;
                                            if (isMultiColumn) {
                                                const algorithmId = multiColumnSortOptions?.algorithmId;
                                                switch (algorithmId) {
                                                    case 2:
                                                        fromColumn.splice(sourceIndex, 1);
                                                        toColumn.splice(destinationIndex, 0, moved = itemToMove);
                                                        if (multiColumnSortOptions.keepSameLen) {
                                                            const totalLen = colsLen && cols.reduce((count, col) => count + col.length, 0);
                                                            const maxItemsPerColumn = colsLen >= totalLen ? 1 : Math.ceil(totalLen/numCol);
                                                            for (let i = 0; i < colsLen; ++i) {
                                                                const col = cols[i];
                                                                const colLen = col.length;
                                                                if (colLen !== maxItemsPerColumn) {
                                                                    let delta = maxItemsPerColumn - colLen;
                                                                    if (delta) {
                                                                        const nextCol = cols[1+i];
                                                                        if (nextCol) {
                                                                            if (delta > 0) {
                                                                                while (delta) {
                                                                                    col.push(nextCol.shift());
                                                                                    --delta;
                                                                                }
                                                                            } else {
                                                                                while (delta) {
                                                                                    nextCol.unshift(col.pop());
                                                                                    ++delta;
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                            newChuncks = [...cols];
                                                        }
                                                        flatten = readColumns(cols, colsLen);
                                                        break;
                                                    case 1:
                                                    default:
                                                        // as old behavior
                                                        const m = readColumns(cols, colsLen);
                                                        const originalPosition = m.findIndex(e => itemToMove === e);
                                                        if (itemDestination) {
                                                            const index = m.findIndex(e => itemDestination === e);
                                                            if (-1 !== index) {
                                                                m.splice(originalPosition, 1);
                                                                m.splice(index, 0, moved = itemToMove);
                                                            }
                                                        } else {
                                                            if (destinationIndex) {
                                                                if (destinationIndex >= toColumn.length) {
                                                                    m.splice(originalPosition, 1);
                                                                    m.push(moved = itemToMove);
                                                                }
                                                            }
                                                        }
                                                        if (moved) {
                                                            flatten = m;
                                                            newChuncks = inColumns(m, numCol);
                                                        }
                                                }
                                            } else {
                                                const col = fromColumn;
                                                col.splice(sourceIndex, 1);
                                                col.splice(destinationIndex, 0, moved = itemToMove);
                                                flatten = [].concat(col);
                                                newChuncks = [col];
                                            }
                                            // console.log({ newChuncks, flatten }, flatten?.map(e => e.num));
                                            if (newChuncks) {
                                                updateState({
                                                    chuncks: newChuncks
                                                });
                                                onSortChange?.(moved, flatten);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        getDragging?.(false, event);
                    }}
                >
                    {state.chuncks.map((itemsGroup, groupN) => {
                        const droppableId = `group-${groupN}`;
                        return (
                            <Droppable
                                index={groupN}
                                key={droppableId}
                                droppableId={droppableId}
                                isDropDisabled={isDropDisabled}
                            >
                                {(drop, dropSnapshot) => (
                                    <div
                                        {...{
                                            ...drop.droppableProps,
                                            ref:
                                                el => {
                                                    referred.droppingNode = isDropDisabled ? null : el;
                                                    drop.innerRef(el);
                                                },
                                            className:
                                                `droppable is-${
                                                    isDropDisabled ? 'disabled' : 'enabled'
                                                } ${
                                                    dropSnapshot.isDraggingOver ? 'is' : 'not'
                                                }-dragging-over`
                                        }}
                                    >
                                        {itemsGroup?.map((item, itemN) => {
                                            const draggableId = `draggable-${groupN}-${itemN}`;
                                            return (
                                                <Draggable
                                                    index={itemN}
                                                    key={draggableId}
                                                    draggableId={draggableId}
                                                    isDragDisabled={!isDraggable}
                                                >
                                                    {(drag, dragSnapshot) => (
                                                        <Memoized
                                                            handleRenderer={handleRenderer}
                                                            itemRenderer={itemRenderer}
                                                            itemIsActiveCheck={itemIsActiveCheck}
                                                            itemIsClickable={itemsAreClickable}
                                                            dragSnapshot={dragSnapshot}
                                                            referred={referred}
                                                            drag={drag}
                                                            kind={kind}
                                                            item={item}
                                                        />
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {drop.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        );
                    })}
                  </DragDropContext>
            ) : (
                handleNoItems
                    ? filterString
                        ? <NoResult className="no-results" />
                        : <NoDataToShow className="no-rows" />
                    : null
            )}
        </div>
    );
};

export default ListGrid3;
